import { useForm } from 'react-hook-form';
import { Link, useParams, useLocation } from 'wouter';
import { AlertTriangle } from 'lucide-react';
import { useCreateActivity } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';

const IP_OPTIONS = [
  { value: 'company', label: 'Company — IP owned by our company' },
  { value: 'client', label: 'Client — IP owned by client (HIGH RISK)' },
  { value: 'joint', label: 'Joint — jointly owned' },
  { value: 'licensed_back', label: 'Licensed back — IP licensed back to us' },
  { value: 'undetermined', label: 'Undetermined — not yet established' },
];

interface FormValues {
  name: string;
  description: string;
  ipOwnership: string;
}

export function ActivityCreatePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [, navigate] = useLocation();
  const { mutate, isPending, error: mutationError } = useCreateActivity();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { ipOwnership: 'company' } });

  const ipOwnership = watch('ipOwnership');

  const onSubmit = (values: FormValues) => {
    mutate(
      {
        projectId: projectId ?? '',
        name: values.name,
        ipOwnership: values.ipOwnership,
        ...(values.description ? { description: values.description } : {}),
      },
      {
        onSuccess: (res) => {
          const id = res?.data?.id;
          navigate(id ? `/activities/${id}` : `/projects/${projectId ?? ''}`);
        },
        onError: (err) => {
          // 409 TFA_NOT_LOCKED error is shown via mutationError
          console.error(err);
        },
      }
    );
  };

  const isTfaError =
    mutationError instanceof Error && mutationError.message.includes('TFA');

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">New activity</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Add an R&D activity to this project
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-1">
            Activity name <span className="text-destructive">*</span>
          </label>
          <input
            id="name"
            type="text"
            {...register('name', { required: 'Activity name is required', maxLength: { value: 255, message: 'Max 255 characters' } })}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
            placeholder="e.g. Novel algorithm development"
          />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-1">
            Description
          </label>
          <textarea
            id="description"
            rows={3}
            {...register('description')}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-y"
            placeholder="Brief description of this R&D activity"
          />
        </div>

        <div>
          <label htmlFor="ipOwnership" className="block text-sm font-medium mb-1">
            IP ownership <span className="text-destructive">*</span>
          </label>
          <select
            id="ipOwnership"
            {...register('ipOwnership', { required: 'IP ownership is required' })}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
          >
            {IP_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {errors.ipOwnership && <p className="mt-1 text-xs text-destructive">{errors.ipOwnership.message}</p>}

          {ipOwnership === 'client' && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-1" />
              <span>
                <strong>HIGH RISK:</strong> Activities where the client holds the IP may not qualify
                for the R&D Tax Incentive. Seek legal advice before lodging claims for such activities.
              </span>
            </div>
          )}

          {ipOwnership === 'undetermined' && (
            <div className="mt-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
              IP ownership must be resolved before this project can be locked for lodgement.
            </div>
          )}
        </div>

        {isTfaError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            The project's TFA panel must be locked before adding activities.
          </div>
        )}

        {mutationError && !isTfaError && (
          <p className="text-sm text-destructive">Failed to create activity. Please try again.</p>
        )}

        <div className="flex gap-3">
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? 'Creating...' : 'Create activity'}
          </Button>
          <Link href={`/projects/${projectId ?? ''}`}>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
